import React, { useState, useMemo } from "react";
import type { BaselineSnapshotRegion } from "../../../api/baseline";

interface ImpactCalculatorProps {
  baselineRegions: BaselineSnapshotRegion[];
  selectedBaCode: string | null;
  onClose: () => void;
}

const LOAD_PRESETS = [
  { label: "Office building", mwh: 500 },
  { label: "1 MW solar farm", mwh: 1927 },
  { label: "10 MW datacenter", mwh: 73584 },
  { label: "100 MW industrial", mwh: 876000 },
];

function formatImpactValue(val: number): string {
  const abs = Math.abs(val);
  if (abs >= 1_000_000) return val.toLocaleString(undefined, { maximumFractionDigits: 0 });
  if (abs >= 1_000) return val.toLocaleString(undefined, { maximumFractionDigits: 1 });
  if (abs >= 1) return val.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 2 });
  if (abs >= 0.01) return val.toFixed(3);
  if (val === 0) return "0";
  return val.toExponential(2);
}

const ImpactCalculator: React.FC<ImpactCalculatorProps> = ({
  baselineRegions,
  selectedBaCode,
  onClose,
}) => {
  const sortedRegions = useMemo(
    () => [...baselineRegions].sort((a, b) => a.ba_code.localeCompare(b.ba_code)),
    [baselineRegions]
  );

  const [baCode, setBaCode] = useState(selectedBaCode || sortedRegions[0]?.ba_code || "");
  const [loadMwh, setLoadMwh] = useState<number>(73584); // 10 MW datacenter default
  const [activePreset, setActivePreset] = useState<string | null>("10 MW datacenter");

  const region = useMemo(
    () => sortedRegions.find((r) => r.ba_code === baCode),
    [sortedRegions, baCode]
  );

  const impacts = useMemo(() => {
    if (!region || !loadMwh) return null;
    return {
      co2_tonnes: ((region.carbon_intensity_kg_per_mwh ?? 0) * loadMwh) / 1000,
      dalys: (region.human_health_dalys_per_mwh ?? 0) * loadMwh,
      species_yr: (region.ecosystem_quality_species_yr_per_mwh ?? 0) * loadMwh,
      usd: (region.resource_scarcity_usd_per_mwh ?? 0) * loadMwh,
    };
  }, [region, loadMwh]);

  return (
    <div className="w-80 bg-surface-800/95 backdrop-blur-sm border border-border rounded-xl shadow-2xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border/50">
        <h3 className="text-body-sm font-semibold text-text-primary">
          What's My Grid Impact?
        </h3>
        <button
          onClick={onClose}
          className="p-0.5 rounded hover:bg-surface-700 text-text-muted hover:text-text-primary transition-colors"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="p-4 space-y-3">
        {/* Region selector */}
        <div>
          <label className="text-[11px] text-text-muted block mb-1">Region</label>
          <select
            value={baCode}
            onChange={(e) => setBaCode(e.target.value)}
            className="input w-full text-body-sm"
          >
            {sortedRegions.map((r) => (
              <option key={r.ba_code} value={r.ba_code}>
                {r.ba_code}
              </option>
            ))}
          </select>
        </div>

        {/* Load input */}
        <div>
          <label className="text-[11px] text-text-muted block mb-1">Annual Consumption (MWh)</label>
          <input
            type="number"
            value={loadMwh || ""}
            onChange={(e) => {
              setLoadMwh(Number(e.target.value));
              setActivePreset(null);
            }}
            placeholder="Enter MWh/year"
            className="input w-full text-body-sm"
          />
        </div>

        {/* Presets */}
        <div className="flex flex-wrap gap-1">
          {LOAD_PRESETS.map((p) => (
            <button
              key={p.label}
              onClick={() => {
                setLoadMwh(p.mwh);
                setActivePreset(p.label);
              }}
              className={`px-2 py-0.5 rounded text-[10px] font-medium transition-colors ${
                activePreset === p.label
                  ? "bg-quantum-500/20 text-quantum-400 border border-quantum-500/50"
                  : "bg-surface-700 text-text-muted border border-border/50 hover:text-text-secondary"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* Results */}
        {impacts && (
          <div className="space-y-2 pt-2 border-t border-border/50">
            <p className="text-[10px] text-text-muted uppercase tracking-wider">
              Estimated Annual Grid Impact
            </p>
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-surface-700/50 rounded-lg px-3 py-2">
                <p className="text-[10px] text-text-muted">Carbon</p>
                <p className="text-body-sm font-bold text-text-primary">
                  {formatImpactValue(impacts.co2_tonnes)}
                </p>
                <p className="text-[10px] text-text-muted">tonnes CO2-eq</p>
              </div>
              <div className="bg-surface-700/50 rounded-lg px-3 py-2">
                <p className="text-[10px] text-text-muted">Human Health</p>
                <p className="text-body-sm font-bold text-health-400">
                  {formatImpactValue(impacts.dalys)}
                </p>
                <p className="text-[10px] text-text-muted">DALYs</p>
              </div>
              <div className="bg-surface-700/50 rounded-lg px-3 py-2">
                <p className="text-[10px] text-text-muted">Ecosystem</p>
                <p className="text-body-sm font-bold text-ecosystem-400">
                  {formatImpactValue(impacts.species_yr)}
                </p>
                <p className="text-[10px] text-text-muted">species.yr</p>
              </div>
              <div className="bg-surface-700/50 rounded-lg px-3 py-2">
                <p className="text-[10px] text-text-muted">Economic</p>
                <p className="text-body-sm font-bold text-green-400">
                  ${formatImpactValue(impacts.usd)}
                </p>
                <p className="text-[10px] text-text-muted">resource scarcity</p>
              </div>
            </div>
            <p className="text-[10px] text-text-muted leading-relaxed">
              Screening estimate based on {baCode} 30-day grid baseline.{" "}
              <a href="/login" className="text-quantum-400 hover:text-quantum-300">
                Sign in
              </a>{" "}
              for full lifecycle analysis.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImpactCalculator;
